import { NextPage } from 'next'
import {
  specialitiesSearchSelectors,
  specialitiesSearchThunks,
} from '@ucheba/store/specialities/search'
import dynamic from 'next/dynamic'
import { getIDeviceComponent } from '@ucheba/utils/helpers/components'
import { IObjectDevicesComponents } from '@ucheba/utils/helpers/components/types'
import React from 'react'
import { NextSeo } from 'next-seo'
import { NextPageContext } from 'next/dist/shared/lib/utils'
import { isSubdomainMatches } from '@ucheba/utils/middlewares/defineLocationMiddleware'
import { Store } from 'redux'
import { specialitiesSearchLimit } from '@ucheba/store/specialities/search/constants'
import { ISpecialitiesSearchPageProps } from '../types'
import {
  getDescriptionWithExamSubjects,
  getFlatTags,
  getTitleWithExamSubjects,
} from '../bll'
import {
  getCurrentTag,
  getDescriptionWithSection,
  getDescriptionWithTag,
  getIsInstitutionSection,
  getTitleWithSection,
  getTitleWithTag,
} from '../../index/bll'
import Custom404 from '../../../../404.page'
import { useRedirectByQueryParams } from '../../../../../../utils/hooks/useRedirectByQueryParams'
import { forAbiturients } from '../../../../../routs'

const devicesPages: IObjectDevicesComponents = {
  desktop: dynamic(() => import('../desktop')),
  touch: dynamic(() => import('../touch')),
}

const SpecialitiesSearchPage: NextPage<ISpecialitiesSearchPageProps> = (props) => {
  const { content, canonical, notFound } = props

  useRedirectByQueryParams({ pathname: forAbiturients.speciality.search.path })

  if (notFound) {
    return <Custom404 />
  }

  return (
    <>
      <NextSeo
        canonical={canonical}
        title={content.seo.meta.title}
        description={content.seo.meta.description}
        openGraph={{}}
        noindex={!content.canIndex}
        nofollow={!content.canIndex}
      />

      {getIDeviceComponent(props, devicesPages)}
    </>
  )
}

SpecialitiesSearchPage.getInitialProps = async (
  ctx: NextPageContext & { store: Store }
): Promise<any> => {
  const { store, query, pathname } = ctx
  const { dispatch, getState } = store

  const { params } = query

  const searchPath = (() => {
    if (Array.isArray(params)) {
      return `/${params.join('/')}`
    }
    return ''
  })()

  await dispatch(
    specialitiesSearchThunks.fetchByFriendlyURL({
      ctx,
      data: {
        url: searchPath,
        limit: specialitiesSearchLimit,
        ...query,
      },
    })
  )

  const facets = specialitiesSearchSelectors.facets(store.getState())
  const tags = getFlatTags(facets)
  const currentTag = getCurrentTag(query)
  const isInstitutionsSection = getIsInstitutionSection(query, pathname)

  const isDomainWWW = isSubdomainMatches(ctx.req, store, 'www')

  const canonical = isDomainWWW
    ? undefined
    : 'https://www.ucheba.ru/for-abiturients/speciality/search'

  const canIndex = specialitiesSearchSelectors.canIndex(getState())
  const seo = specialitiesSearchSelectors.seo(getState())

  return {
    canonical,
    content: {
      seo: {
        meta: {
          title:
            seo?.h1 ||
            getTitleWithExamSubjects({
              query,
              facets,
            }) ||
            getTitleWithTag({
              tags,
              currentTag,
              isInstitutionsSection,
            }) ||
            getTitleWithSection({ isInstitutionsSection }),
          description:
            getDescriptionWithExamSubjects({
              query,
              facets,
            }) ||
            getDescriptionWithTag({
              tags,
              currentTag,
              isInstitutionsSection,
            }) ||
            getDescriptionWithSection({ isInstitutionsSection }),
        },
      },
      canIndex,
    },
  }
}

export default SpecialitiesSearchPage
